import { z } from 'zod';
import {
  type ActivityType,
  BUSINESS_KINDS,
  type BusinessKind,
  LEAD_SEGMENTS,
  type LeadSegment,
  type ActorType,
  CALL_OUTCOMES,
  CONSENT_BASES,
  CONTACT_ROLES,
  CONTACT_VERIFICATION_STATUSES,
  type ConsentBasis,
  type ContactRole,
  type ContactVerificationStatus,
  type CustomFieldType,
  EQUIPMENT_CONDITIONS,
  EQUIPMENT_TYPES,
  type EmailDirection,
  type EmailStatus,
  type EquipmentCondition,
  type EquipmentType,
  FUEL_TYPES,
  type FuelType,
  MEETING_OUTCOMES,
  type NextAction,
  OPERATING_STATUSES,
  OWNERSHIP_TYPES,
  type OperatingStatus,
  type OwnershipType,
  PIZZA_FOCUS,
  PREFERRED_CONTACT_METHODS,
  PURCHASE_TIMEFRAMES,
  type PizzaFocus,
  type PreferredContactMethod,
  type PurchaseTimeframe,
  type ScoreCategory,
  TASK_TYPES,
} from '../constants';
import { NullableText, RequiredText } from './common';
import { ReminderMinutes } from './tasks';

/*
 * Lead profile contracts: manual lead creation, the profile DTO, and everything attached directly to a lead
 * (contacts, equipment, interests, tags, notes, logged activities, signals, proposals, custom fields).
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OptionalEmail = NullableText(320)
  .refine((v) => v === null || EMAIL_RE.test(v), 'Enter a valid email address')
  .optional();
const IsoDateTime = z.iso.datetime({ offset: true });
const Money = z.number().min(0).max(1_000_000_000).nullable();

/** Business (location) attributes a user can enter or edit. */
export const LeadFields = z.object({
  name: RequiredText(200),
  /** Pizzeria (the default), dealer or rep — which list the business appears in. */
  kind: z.enum(BUSINESS_KINDS).optional(),
  /** Leads, Prospects, Clients or Test Kitchens list (pizzerias). */
  segment: z.enum(LEAD_SEGMENTS).optional(),
  category: NullableText(120).optional(),
  restaurantType: NullableText(120).optional(),
  pizzaFocus: z.enum(PIZZA_FOCUS).optional(),
  ownershipType: z.enum(OWNERSHIP_TYPES).optional(),
  locationCount: z.number().int().min(1).max(100_000).nullable().optional(),
  openedOn: z.iso.date().nullable().optional(),
  operatingStatus: z.enum(OPERATING_STATUSES).optional(),
  description: NullableText(5000).optional(),
  addressLine1: NullableText(200).optional(),
  addressLine2: NullableText(200).optional(),
  city: NullableText(120).optional(),
  region: NullableText(120).optional(),
  postalCode: NullableText(20).optional(),
  country: NullableText(60).optional(),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  phone: NullableText(40).optional(),
  website: NullableText(300).optional(),
  email: OptionalEmail,
  estimatedValue: Money.optional(),
  currency: z.string().trim().length(3).optional(),
  purchaseTimeframe: z.enum(PURCHASE_TIMEFRAMES).optional(),
  budget: Money.optional(),
  groupId: z.uuid().nullable().optional(),
});
export type LeadFields = z.infer<typeof LeadFields>;

export const ContactBody = z.object({
  firstName: NullableText(100).optional(),
  lastName: NullableText(100).optional(),
  role: z.enum(CONTACT_ROLES).optional(),
  title: NullableText(120).optional(),
  email: OptionalEmail,
  phone: NullableText(40).optional(),
  preferredContactMethod: z.enum(PREFERRED_CONTACT_METHODS).optional(),
  isPrimary: z.boolean().optional(),
  isDecisionMaker: z.boolean().optional(),
  verificationStatus: z.enum(CONTACT_VERIFICATION_STATUSES).optional(),
  consentBasis: z.enum(CONSENT_BASES).optional(),
  consentObtainedAt: IsoDateTime.nullable().optional(),
  consentSource: NullableText(300).optional(),
  notes: NullableText(2000).optional(),
});
export type ContactBody = z.infer<typeof ContactBody>;

export const UpdateContactBody = ContactBody.extend({ version: z.number().int().min(1) });
export type UpdateContactBody = z.infer<typeof UpdateContactBody>;

export const EquipmentBody = z.object({
  equipmentType: z.enum(EQUIPMENT_TYPES),
  brand: NullableText(120).optional(),
  model: NullableText(120).optional(),
  fuelType: z.enum(FUEL_TYPES).optional(),
  installYear: z.number().int().min(1900).max(2100).nullable().optional(),
  condition: z.enum(EQUIPMENT_CONDITIONS).optional(),
  isCurrent: z.boolean().optional(),
  notes: NullableText(2000).optional(),
});
export type EquipmentBody = z.infer<typeof EquipmentBody>;

export const UpdateEquipmentBody = EquipmentBody.partial();
export type UpdateEquipmentBody = z.infer<typeof UpdateEquipmentBody>;

/** POST /api/leads — manual lead entry. */
export const CreateLeadBody = LeadFields.extend({
  contacts: z.array(ContactBody).max(20).default([]),
  equipment: z.array(EquipmentBody).max(20).default([]),
  interests: z.array(z.string().trim().min(1).max(60)).max(50).default([]),
  tagIds: z.array(z.uuid()).max(50).default([]),
  /** Omitted → territory default owner, else the creating rep; null → unassigned. */
  ownerId: z.uuid().nullable().optional(),
  sourceId: z.uuid().nullable().optional(),
});
export type CreateLeadBody = z.infer<typeof CreateLeadBody>;
export type CreateLeadInput = z.input<typeof CreateLeadBody>;
export type CreateLeadResponse = { id: string; warnings: string[]; duplicatesFlagged: number };

/** PATCH /api/leads/:id — any subset of fields plus the version the client loaded. */
export const UpdateLeadBody = LeadFields.partial().extend({
  version: z.number().int().min(1),
  stageKey: z.string().trim().min(1).max(60).optional(),
  relationshipKey: z.string().trim().min(1).max(60).optional(),
  /** Pin the tier by hand (a tier key), or null to follow the score again. */
  tierOverrideKey: z.string().trim().min(1).max(60).nullable().optional(),
  ownerId: z.uuid().nullable().optional(),
  territoryId: z.uuid().nullable().optional(),
});
export type UpdateLeadBody = z.infer<typeof UpdateLeadBody>;
export const CreateNoteBody = z.object({
  body: RequiredText(20_000),
  contactId: z.uuid().nullable().optional(),
  opportunityId: z.uuid().nullable().optional(),
  isPinned: z.boolean().optional(),
});
export type CreateNoteBody = z.infer<typeof CreateNoteBody>;

export const UpdateNoteBody = z.object({ body: RequiredText(20_000) });
export type UpdateNoteBody = z.infer<typeof UpdateNoteBody>;

/**
 * The next step scheduled together with a logged call / meeting / reply ("call back Tuesday at 10"). Created in the
 * same transaction as a task on the same lead (and the logged contact / deal), with the usual task rules.
 */
export const FollowUpBody = z.object({
  type: z.enum(TASK_TYPES).default('follow_up'),
  title: RequiredText(300),
  description: NullableText(5000).optional(),
  dueAt: IsoDateTime,
  endAt: IsoDateTime.nullable().optional(),
  allDay: z.boolean().default(false),
  reminderMinutes: ReminderMinutes.nullable().optional(),
  /** Defaults to the current user. Assigning to someone else requires lead:assign. */
  assignedTo: z.uuid().nullable().optional(),
});
export type FollowUpBody = z.input<typeof FollowUpBody>;

const ActivityCommon = {
  summary: NullableText(300).optional(),
  body: NullableText(20_000).optional(),
  contactId: z.uuid().nullable().optional(),
  opportunityId: z.uuid().nullable().optional(),
  /** When it happened (defaults to now); may not be in the future. */
  occurredAt: IsoDateTime.optional(),
  followUp: FollowUpBody.optional(),
  /** Relationship status to set instead of the automatic move (e.g. 'warm' after a good call). */
  relationshipKey: z.string().trim().min(1).max(60).optional(),
};

/**
 * POST /api/leads/:id/activities — log what happened outside the CRM. Each kind has the right counter,
 * stage/relationship and scoring side effects (see modules/leads/activities.ts).
 */
export const LogActivityBody = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('call'),
    direction: z.enum(['outbound', 'inbound']).default('outbound'),
    outcome: z.enum(CALL_OUTCOMES),
    ...ActivityCommon,
  }),
  z.object({
    kind: z.literal('meeting'),
    outcome: z.enum(MEETING_OUTCOMES),
    /** For outcome 'scheduled': when the meeting/demo will take place. */
    scheduledFor: IsoDateTime.nullable().optional(),
    ...ActivityCommon,
  }),
  z.object({ kind: z.literal('email_received'), ...ActivityCommon }),
  z.object({
    kind: z.literal('not_interested'),
    reason: RequiredText(500),
    contactId: ActivityCommon.contactId,
    occurredAt: ActivityCommon.occurredAt,
  }),
  z.object({ kind: z.literal('other'), ...ActivityCommon, summary: RequiredText(300) }),
]);
export type LogActivityBody = z.infer<typeof LogActivityBody>;
export type LogActivityInput = z.input<typeof LogActivityBody>;
export type LogActivityKind = LogActivityBody['kind'];

/** Lead-derived state after a mutation, so the UI can update the next-best-action callout immediately. */
export type LeadDerivedDTO = {
  score: number;
  tierKey: string;
  nextAction: NextAction | null;
  nextActionReason: string | null;
};
/** `followUpTaskId`: the task created from `followUp`, if one was given. */
export type LogActivityResponse = { activityId: string; lead: LeadDerivedDTO; followUpTaskId: string | null };

export const RecordSignalBody = z.object({
  ruleKey: z.string().trim().min(1).max(60),
  note: NullableText(1000).optional(),
  occurredAt: IsoDateTime.optional(),
});
export type RecordSignalBody = z.infer<typeof RecordSignalBody>;

export const TimelineQuery = z.object({
  /** Comma-separated activity types; empty → all. */
  types: z.preprocess(
    (v) => (typeof v === 'string' ? (v === '' ? [] : v.split(',')) : v),
    z.array(z.string().max(60)).max(60),
  ).optional(),
  /** Opaque keyset cursor from the previous page. */
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type TimelineQuery = z.infer<typeof TimelineQuery>;

export const SetCustomFieldsBody = z.object({
  /** definitionId → value; null/'' clears. Dates as YYYY-MM-DD. */
  values: z.record(z.uuid(), z.union([z.string().max(2000), z.number(), z.boolean(), z.null()])),
});
export type SetCustomFieldsBody = z.infer<typeof SetCustomFieldsBody>;

// ---------------------------------------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------------------------------------

export type UserRefDTO = { id: string; name: string };

export type ContactDTO = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  /** Display name ("Marco Rossi"), falling back to email/phone. */
  displayName: string;
  role: ContactRole;
  title: string | null;
  email: string | null;
  phone: string | null;
  preferredContactMethod: PreferredContactMethod;
  isPrimary: boolean;
  isDecisionMaker: boolean;
  verificationStatus: ContactVerificationStatus;
  lastVerifiedAt: string | null;
  consentBasis: ConsentBasis;
  consentObtainedAt: string | null;
  consentSource: string | null;
  notes: string | null;
  /** Active suppression on this contact's address (unsubscribed, bounced, DNC…). */
  suppression: { reason: string; since: string } | null;
  version: number;
  createdAt: string;
};

export type EquipmentDTO = {
  id: string;
  equipmentType: EquipmentType;
  brand: string | null;
  model: string | null;
  fuelType: FuelType;
  installYear: number | null;
  /** Whole years since installYear (null when unknown). */
  ageYears: number | null;
  condition: EquipmentCondition;
  isCurrent: boolean;
  notes: string | null;
  createdAt: string;
};

export type ScoreComponentDTO = {
  ruleKey: string;
  label: string;
  category: ScoreCategory;
  points: number;
  detail: string | null;
  /** Set when the component comes from a recorded signal (voidable from the profile). */
  signalId: string | null;
};

export type ScoreCategoryDTO = { category: ScoreCategory; label: string; points: number; components: ScoreComponentDTO[] };

export type ScoreDTO = {
  total: number;
  rawTotal: number;
  tierKey: string;
  computedAt: string | null;
  categories: ScoreCategoryDTO[];
};

export type SignalDTO = {
  id: string;
  ruleKey: string;
  label: string;
  category: ScoreCategory | null;
  points: number | null;
  occurredAt: string;
  source: string;
  note: string | null;
  createdBy: UserRefDTO | null;
  /** Whether it currently contributes to the score (not expired / within max occurrences / rule enabled). */
  counted: boolean;
};

export type ProposalDTO = {
  id: string;
  entityType: 'business' | 'contact';
  entityId: string;
  field: string;
  currentValue: string | null;
  proposedValue: string | null;
  source: string;
  sourceDetail: string | null;
  createdAt: string;
};

export type LeadDuplicateDTO = {
  candidateId: string;
  otherBusinessId: string;
  otherName: string;
  otherCity: string | null;
  confidence: number;
  detectedAt: string;
};

export type DataQualityIssueDTO = { code: string; severity: 'warning' | 'info'; message: string };

export type SourceRecordDTO = {
  id: string;
  provider: string;
  sourceName: string | null;
  externalId: string | null;
  sourceUrl: string | null;
  fetchedAt: string;
  importJobId: string | null;
  discoveryResultId: string | null;
};

export type CustomFieldValueDTO = {
  definitionId: string;
  key: string;
  label: string;
  type: CustomFieldType;
  options: string[];
  isRequired: boolean;
  value: string | number | boolean | null;
};

export type NoteDTO = {
  id: string;
  body: string;
  isPinned: boolean;
  contactId: string | null;
  opportunityId: string | null;
  author: UserRefDTO | null;
  createdAt: string;
  updatedAt: string;
};

export type GroupLocationDTO = {
  id: string;
  name: string;
  city: string | null;
  addressLine1: string | null;
  score: number;
  tierKey: string;
};

/** GET /api/leads/:id — everything the profile needs in one request. */
export type LeadDetailDTO = {
  id: string;
  version: number;
  name: string;
  kind: BusinessKind;
  segment: LeadSegment;
  category: string | null;
  restaurantType: string | null;
  pizzaFocus: PizzaFocus;
  ownershipType: OwnershipType;
  locationCount: number | null;
  openedOn: string | null;
  operatingStatus: OperatingStatus;
  description: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string;
  latitude: number | null;
  longitude: number | null;
  timezone: string | null;
  phone: string | null;
  phoneE164: string | null;
  website: string | null;
  websiteDomain: string | null;
  email: string | null;
  emailSuppression: { reason: string; since: string } | null;
  estimatedValue: number | null;
  currency: string;
  purchaseTimeframe: PurchaseTimeframe;
  budget: number | null;

  stageId: string;
  stageChangedAt: string | null;
  relationshipId: string;
  ownerId: string | null;
  territoryId: string | null;
  sourceId: string | null;
  sourceRef: string | null;
  discoveredAt: string | null;

  /** The tier shown: the one set by hand when `tierOverrideKey` is set, else the score's tier (`score.tierKey`). */
  tierKey: string;
  tierOverrideKey: string | null;
  score: ScoreDTO;
  nextAction: NextAction | null;
  nextActionReason: string | null;
  nextActionPriority: number | null;
  derivedAt: string | null;
  counters: {
    lastContactedAt: string | null;
    lastEmailSentAt: string | null;
    lastResponseAt: string | null;
    lastMeaningfulAt: string | null;
    nextFollowUpAt: string | null;
    outreachAttempts: number;
    responseCount: number;
  };

  lastVerifiedAt: string | null;
  lastVerifiedBy: UserRefDTO | null;
  doNotContact: { reason: string | null; at: string | null; by: UserRefDTO | null } | null;
  deleted: { at: string; by: UserRefDTO | null } | null;
  mergedInto: { id: string; name: string } | null;
  createdAt: string;
  createdBy: UserRefDTO | null;
  updatedAt: string;

  contacts: ContactDTO[];
  equipment: EquipmentDTO[];
  /** Product category keys (labels in meta.productCategories). */
  interests: string[];
  tagIds: string[];
  customFields: CustomFieldValueDTO[];
  signals: SignalDTO[];
  proposals: ProposalDTO[];
  duplicates: LeadDuplicateDTO[];
  dataQuality: DataQualityIssueDTO[];
  group: {
    id: string;
    name: string;
    websiteDomain: string | null;
    locationCount: number | null;
    otherLocations: GroupLocationDTO[];
  } | null;
  sourceRecords: SourceRecordDTO[];
  pinnedNotes: NoteDTO[];
  counts: { openTasks: number; openOpportunities: number; emails: number; notes: number };
  /** Whether the signed-in user may edit this lead (role + edit scope). */
  canEdit: boolean;
};

export type TimelineItemDTO = {
  id: string;
  type: ActivityType;
  summary: string;
  body: string | null;
  outcome: string | null;
  direction: EmailDirection | null;
  isMeaningful: boolean;
  isOutreach: boolean;
  occurredAt: string;
  actorType: ActorType;
  actor: UserRefDTO | null;
  contact: { id: string; name: string } | null;
  opportunityId: string | null;
  taskId: string | null;
  email: { id: string; subject: string; status: EmailStatus; toEmail: string } | null;
  note: NoteDTO | null;
  metadata: Record<string, unknown>;
};

export type TimelinePageDTO = { items: TimelineItemDTO[]; nextCursor: string | null };
